<template>
  <div
    class="completion-highlight"
    :class="{ 'completion-highlight--clickable': !canExpand }"
    @click="openIfFullyVisible"
  >
    <button
      v-if="canExpand"
      class="completion-highlight__open iconfont inext"
      type="button"
      aria-label="Open highlight"
      @click.stop="emit('open')"
    ></button>
    <span
      v-else
      class="completion-highlight__open iconfont inext"
      aria-hidden="true"
    ></span>
    <p
      ref="passage"
      class="completion-highlight__passage"
      :class="{ 'completion-highlight__passage--clamped': !expanded }"
    >
      {{ quote }}
    </p>
    <button
      v-if="canExpand"
      class="completion-highlight__more"
      type="button"
      @click.stop="expanded = true"
    >
      Show more
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

defineProps<{
  quote: string
}>()

const emit = defineEmits<{
  (event: 'open'): void
}>()

const passage = ref<HTMLElement | null>(null)
const expanded = ref(false)
const overflows = ref(false)
const canExpand = computed(() => overflows.value && !expanded.value)

onMounted(() => {
  // Offer the nested expander only when the five-line clamp hides text.
  if (passage.value) {
    overflows.value = passage.value.scrollHeight > passage.value.clientHeight
  }
})

function openIfFullyVisible(): void {
  if (!canExpand.value) {
    emit('open')
  }
}
</script>

<style lang="scss" scoped>
.completion-highlight {
  position: relative;
  padding: 12px 40px 12px 16px;
  border-radius: 8px;
  background: rgba(#ffc906, 0.1);
  color: $c-black-15;
  font-size: 16px;
  line-height: 1.3;
  transition: background-color 0.15s;

  &--clickable {
    cursor: pointer;

    @include hover {
      background: rgba(#ffc906, 0.16);
    }
  }

  &__open {
    position: absolute;
    top: 12px;
    right: 8px;
    display: grid;
    width: 24px;
    height: 24px;
    place-items: center;
    color: $c-black-2;
    font-size: 16px;
  }

  &__passage {
    margin: 0;
    color: $c-black-15;

    &--clamped {
      display: -webkit-box;
      overflow: hidden;
      line-clamp: 5;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 5;
    }
  }

  &__more {
    padding: 0;
    color: $c-black-15;
    text-decoration: underline;
  }
}

.night-mode .completion-highlight {
  color: $c-dark-4;

  &__passage {
    color: $c-dark-4;
  }

  &__open {
    color: $c-dark-4;
  }

  &__more {
    color: $c-dark-8;
  }
}
</style>
